import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { apiFetch } from '../lib/api';

/**
 * Auth callback handler — /auth/callback
 *
 * Runs after:
 *   - Email/password signup → email verification link clicked
 *   - Google / Apple OAuth → provider redirects back here
 *
 * detectSessionInUrl is disabled so we own the exchange ourselves.
 * This eliminates Web Lock races and gives us explicit error handling.
 *
 * Routing logic after a successful code exchange:
 *   1. If user_metadata has the fields SignupPage stashed (phone +
 *      consents), call /api/auth/complete-signup to persist them, then
 *      go straight to /dashboard.
 *   2. Otherwise (Google/Apple OAuth users) → /auth/consent to
 *      collect phone + consents.
 */
export default function AuthCallbackPage() {
  const navigate        = useNavigate();
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    const slowTimer = setTimeout(() => setSlow(true), 4000);
    const hardLimit = setTimeout(() => navigate('/login', { replace: true }), 20000);

    const code = new URLSearchParams(window.location.search).get('code');
    if (!code) {
      console.error('[AuthCallback] no code in URL:', window.location.href);
      navigate('/login', { replace: true });
      return;
    }

    (async () => {
      const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
      clearTimeout(hardLimit);

      if (exchangeError) {
        console.error('[AuthCallback] exchange failed:', exchangeError.message);
        navigate('/login', { replace: true });
        return;
      }

      // Read the metadata SignupPage stashed (if this is an email/password
      // signup completing email verification).
      const { data: { user } } = await supabase.auth.getUser();
      const meta = user?.user_metadata as Record<string, unknown> | undefined;

      const phone           = typeof meta?.phone === 'string' ? meta.phone : '';
      const consentSms      = meta?.consent_sms === true;
      const consentTerms    = meta?.consent_terms === true;
      const firstName       = typeof meta?.first_name === 'string' ? meta.first_name : '';
      const lastName        = typeof meta?.last_name === 'string' ? meta.last_name : '';
      const marketingOptIn  = meta?.marketing_opt_in === true;
      const referredByCode  = typeof meta?.referred_by_code === 'string' ? meta.referred_by_code : undefined;

      // Email/password signup stashes phone + both required consents.
      // OAuth users won't have these — send them to /auth/consent.
      if (phone && consentSms && consentTerms) {
        try {
          await apiFetch('/api/auth/complete-signup', {
            method: 'POST',
            body:   JSON.stringify({
              firstName, lastName, phone,
              consentSms, consentTerms, marketingOptIn,
              referredByCode,
            }),
          });
          // Clear the metadata so we don't re-run this on subsequent logins
          await supabase.auth.updateUser({
            data: {
              phone:            null,
              consent_sms:      null,
              consent_terms:    null,
              marketing_opt_in: null,
              referred_by_code: null,
            },
          });
        } catch (err) {
          // Non-fatal — user can still complete consent manually if this fails
          console.error('[AuthCallback] complete-signup failed:', err);
          navigate('/auth/consent', { replace: true });
          return;
        }
        navigate('/dashboard', { replace: true });
      } else {
        navigate('/auth/consent', { replace: true });
      }
    })();

    return () => { clearTimeout(slowTimer); clearTimeout(hardLimit); };
  }, [navigate]);

  return (
    <div className="min-h-screen bg-cream flex flex-col items-center justify-center gap-4 px-6 text-center">
      <div className="w-8 h-8 border-2 border-sage border-t-transparent rounded-full animate-spin" />
      <p className="font-body text-sm text-gray-400">Signing you in to MomentSent…</p>
      {slow && (
        <p className="font-body text-xs text-gray-400 max-w-xs animate-fade-in">
          Taking a little longer than usual, almost there.
        </p>
      )}
    </div>
  );
}
